import {storageAccount} from "./config"

export const storageVersion = "2022-11-02"
export const blobEndpoint = `https://${storageAccount}.blob.core.windows.net`

export interface BlobItem {
  name: string
  size: number
  lastModified: Date
  metadata: Record<string, string>
}

export interface BlobListPage {
  blobs: BlobItem[]
  prefixes: string[]
  nextMarker: string
}

export class StorageError extends Error {
  constructor(readonly status: number, readonly container: string, message: string) {
    super(message)
  }
}

function childText(parent: Element, tag: string): string {
  return parent.getElementsByTagName(tag)[0]?.textContent ?? ""
}

/** Parses one page of a List Blobs response. */
export function parseBlobList(xml: string): BlobListPage {
  const doc = new DOMParser().parseFromString(xml, "application/xml")
  const blobs = Array.from(doc.getElementsByTagName("Blob")).map((blob) => {
    const metadata: Record<string, string> = {}
    for (const entry of Array.from(blob.getElementsByTagName("Metadata")[0]?.children ?? [])) {
      metadata[entry.tagName.toLowerCase()] = entry.textContent ?? ""
    }
    return {
      name: childText(blob, "Name"),
      size: Number(childText(blob, "Content-Length")),
      lastModified: new Date(childText(blob, "Last-Modified")),
      metadata,
    }
  })
  const prefixes = Array.from(doc.getElementsByTagName("BlobPrefix")).map((p) => childText(p, "Name"))
  const nextMarker = doc.getElementsByTagName("NextMarker")[0]?.textContent ?? ""
  return {blobs, prefixes, nextMarker}
}

/** Lists a container (under `prefix`, one level when `delimiter` is given), following every page. */
export async function listBlobs(
  token: string,
  container: string,
  options: {prefix?: string; delimiter?: string} = {},
): Promise<{blobs: BlobItem[]; prefixes: string[]}> {
  const blobs: BlobItem[] = []
  const prefixes: string[] = []
  let marker = ""
  do {
    const query = new URLSearchParams({restype: "container", comp: "list", include: "metadata"})
    if (options.prefix) query.set("prefix", options.prefix)
    if (options.delimiter) query.set("delimiter", options.delimiter)
    if (marker) query.set("marker", marker)
    const response = await fetch(`${blobEndpoint}/${container}?${query}`, {
      headers: {"Authorization": `Bearer ${token}`, "x-ms-version": storageVersion},
    })
    if (!response.ok) {
      throw new StorageError(response.status, container, `Listing ${container} failed: ${response.status} ${response.statusText}`)
    }
    const page = parseBlobList(await response.text())
    blobs.push(...page.blobs)
    prefixes.push(...page.prefixes)
    marker = page.nextMarker
  } while (marker)
  return {blobs, prefixes}
}
